import { useEffect, useState, type ComponentProps, type ReactNode } from 'react';
import { Globe } from 'lucide-react';
import type { TaskContextRef, TaskContextSearchHit } from '@shared/types/tasks';
import { TASK_CONTEXT_TYPE_LABELS, webPageContextId } from '@shared/task-contexts';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { searchTaskContexts } from '@/lib/tasks-api';
import { createLabelNow } from './label-actions';
import { AssigneePicker, LabelPicker as BaseLabelPicker } from './TaskMemberLabelPickers';
import { useDebouncedValue } from '@/lib/use-debounced-value';
import { CONTEXT_ICONS } from './task-ui';

export { AssigneePicker };

/** Toggle Labels on a task; a new label is ticked at once and saved behind the scenes. */
export function LabelPicker(props: Omit<ComponentProps<typeof BaseLabelPicker>, 'onCreate'>) {
  return <BaseLabelPicker {...props} onCreate={(name, color) => createLabelNow(props.labels, name, color)} />;
}

/** Link a jflo thing to a task (same search as `@`), the page you're on, or a pasted web address. */
export function ContextPicker({
  onPick,
  currentPage,
  defaultOpen = false,
  onOpenChange,
  children,
}: {
  onPick: (ref: TaskContextRef & { title?: string }) => void;
  /** Offered first: "This page" when you're on something with a Context. */
  currentPage?: (TaskContextRef & { title: string }) | null;
  /** Open as soon as it appears (picked from the properties' + menu). */
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  children: ReactNode;
}) {
  const [open, setOpenState] = useState(defaultOpen);
  const setOpen = (next: boolean) => {
    setOpenState(next);
    onOpenChange?.(next);
  };
  const [search, setSearch] = useState('');
  // Results remember the query they answer: searching is "a query we haven't answered yet".
  const [result, setResult] = useState<{ query: string; hits: TaskContextSearchHit[] } | null>(null);
  const query = useDebouncedValue(search.trim(), 250);
  // A pasted address outside jflo is linked as a web page rather than searched for.
  const webPage = /^https?:\/\//i.test(search.trim()) ? webPageContextId(search) : null;
  const searchable = query.length >= 2 && !webPage;
  const loading = searchable && result?.query !== query;
  const hits = searchable && result?.query === query ? result.hits : [];

  useEffect(() => {
    if (!searchable) return undefined;
    let cancelled = false;
    void searchTaskContexts(query).then((res) => {
      if (!cancelled) setResult({ query, hits: res.data?.results ?? [] });
    });
    return () => {
      cancelled = true;
    };
  }, [query, searchable]);

  const pick = (ref: TaskContextRef & { title?: string }) => {
    onPick(ref);
    setOpen(false);
    setSearch('');
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput placeholder="Search batches, products… or paste a link" value={search} onValueChange={setSearch} />
          <CommandList>
            {currentPage && !search ? (
              <CommandGroup heading="This page">
                <CommandItem value="this-page" onSelect={() => pick(currentPage)}>
                  {(() => {
                    const Icon = CONTEXT_ICONS[currentPage.type];
                    return <Icon className="size-4" />;
                  })()}
                  <span className="truncate">{currentPage.title}</span>
                </CommandItem>
              </CommandGroup>
            ) : null}
            {webPage ? (
              <CommandGroup heading={TASK_CONTEXT_TYPE_LABELS.url}>
                <CommandItem value="web-page" onSelect={() => pick({ type: 'url', id: webPage })}>
                  <Globe className="size-4" />
                  <span className="truncate">{webPage}</span>
                </CommandItem>
              </CommandGroup>
            ) : null}
            {searchable && !loading && hits.length === 0 ? <CommandEmpty>Nothing found.</CommandEmpty> : null}
            {hits.length ? (
              <CommandGroup heading="Results">
                {hits.map((hit) => {
                  const Icon = CONTEXT_ICONS[hit.type];
                  return (
                    <CommandItem key={`${hit.type}:${hit.id}`} value={`${hit.type}:${hit.id}`} onSelect={() => pick(hit)}>
                      <Icon className="size-4" />
                      <span className="min-w-0 flex-1 truncate">
                        {hit.title}
                        {hit.subtitle ? <span className="text-muted-foreground"> · {hit.subtitle}</span> : null}
                      </span>
                      <span className="text-xs text-muted-foreground">{TASK_CONTEXT_TYPE_LABELS[hit.type]}</span>
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ) : null}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
